// UTC days, and what they are on the reader's and a country's wall clock.

import { cityOf, timeAtViewer } from "@/components/emc/time";
//
// The tracking screens count in UTC days because everything they read does:
// the warmup's daily budget, rule 4's daily judgement, the 2.3 aggregate and
// the anomaly profile all turn over at UTC midnight. A person reads a clock,
// though, so a screen that shows a UTC day also says where that day begins in
// the country it is about — "a nap Budapesten 02:00-kor kezdődik".

/** "YYYY-MM-DD" of a moment, in UTC. */
export function utcDay(moment: Date): string {
  return moment.toISOString().slice(0, 10);
}

export function addDays(day: string, count: number): string {
  const date = new Date(`${day}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + count);
  return utcDay(date);
}

/** Every day from `from` to `to`, both included, oldest first. */
export function daysFrom(from: string, to: string): string[] {
  const days: string[] = [];
  for (let day = from; day <= to && days.length < 400; day = addDays(day, 1)) days.push(day);
  return days;
}

export type Preset = "today" | "yesterday" | "7" | "14" | "30" | "custom";

export interface DayRange {
  preset: Preset;
  from: string;
  to: string;
}

export const PRESETS: { value: Preset; label: string }[] = [
  { value: "today", label: "Ma" },
  { value: "yesterday", label: "Tegnap" },
  { value: "7", label: "7 nap" },
  { value: "14", label: "14 nap" },
  { value: "30", label: "30 nap" },
  { value: "custom", label: "Egyéni" },
];

export function presetRange(
  preset: Preset,
  today: string,
  custom?: { from: string; to: string },
): DayRange {
  switch (preset) {
    case "today":
      return { preset, from: today, to: today };
    case "yesterday":
      return { preset, from: addDays(today, -1), to: addDays(today, -1) };
    case "7":
    case "14":
    case "30":
      return { preset, from: addDays(today, -(Number(preset) - 1)), to: today };
    default: {
      const from = custom?.from || addDays(today, -6);
      const to = custom?.to || today;
      return from <= to ? { preset, from, to } : { preset, from: to, to: from };
    }
  }
}

export interface Boundary {
  /** The IANA name, e.g. `Europe/Budapest`. */
  zone: string;
  countries: string[];
  /** What UTC midnight of the day reads on that clock: "02:00". */
  local: string;
}

/** Where a UTC day starts on each country's wall clock. Countries on the same
 * clock that day are one line — KIS is five countries on two clocks. */
export function dayBoundary(
  countries: { country: string; timezone: string }[],
  day: string,
): Boundary[] {
  const midnight = new Date(`${day}T00:00:00Z`);
  const found = new Map<string, Boundary>();
  for (const { country, timezone } of countries) {
    let local: string;
    try {
      local = new Intl.DateTimeFormat("hu-HU", {
        timeZone: timezone,
        hour: "2-digit",
        minute: "2-digit",
        hourCycle: "h23",
      }).format(midnight);
    } catch {
      local = "?";
    }
    const entry = found.get(local);
    if (entry) {
      entry.countries.push(country);
    } else {
      found.set(local, { zone: timezone, countries: [country], local });
    }
  }
  return [...found.values()];
}

/** "Europe/Budapest" → "Budapest". */
export function zoneCity(zone: string): string {
  return cityOf(zone);
}

/** One sentence: where the counted day starts, on the reader's clock first
 * and then per country clock — UTC is not on the screen (owner, 2026-10-26). */
export function boundaryText(boundaries: Boundary[], day: string, viewer?: string): string {
  const here = timeAtViewer(new Date(`${day}T00:00:00Z`), viewer);
  if (boundaries.length === 0) return `A nap nálad ${here}-kor kezdődik.`;
  const parts = boundaries.map(
    (boundary) => `${boundary.countries.join(", ")}: ${cityOf(boundary.zone)} ${boundary.local}`,
  );
  return `A nap nálad ${here}-kor kezdődik — ${parts.join(" · ")}.`;
}
